import React, { useEffect, useState } from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom"; // useNavigate 임포트

// 네이버 지도 API 타입 선언
declare global {
  interface Window {
    naver: any;
  }
}

interface ContactData {
  title: string;
  address: string;
  email: string;
  phone: string;
  buttonText: string;
  companyName: string;
  latitude: number;
  longitude: number;
}

const NAVER_CLIENT_ID = "5cwhpq50um"; // 네이버 지도 API Client ID

const loadNaverMapScript = () => {
  if (document.getElementById("naver-map-script")) return;

  const script = document.createElement("script");
  script.id = "naver-map-script";
  script.src = `https://openapi.map.naver.com/openapi/v3/maps.js?ncpClientId=${NAVER_CLIENT_ID}`;
  script.async = true;
  
  script.onload = () => {
    console.log("Naver Maps API loaded successfully");
  };
  
  script.onerror = () => {
    console.error("Failed to load Naver Maps API");
  };
  
  document.body.appendChild(script);
};

const Contact: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate(); // navigate 함수 생성

  const [contactData, setContactData] = useState<ContactData>({
    title: 'CONTACT',
    address: '서울특별시 영등포구 양평동3가 16, 612호',
    email: 'che37412@hanmail.net',
    phone: '02-2088-7388',
    buttonText: '자세히 알아보기',
    companyName: '씨유미디어그룹',
    latitude: 37.525791,
    longitude: 126.88667,
  });

  useEffect(() => {
    // API에서 연락처 데이터 불러오기
    const loadContactData = async () => {
      try {
        const { sidebarApi } = await import('../services/api');
        const data = await sidebarApi.get();
        if (data) {
          setContactData(prev => ({
            ...prev,
            title: 'CONTACT',
            address: data.address || prev.address,
            phone: data.phone || prev.phone,
            email: data.email || prev.email,
            buttonText: '자세히 알아보기',
            companyName: data.companyName || prev.companyName,
            latitude: data.latitude || prev.latitude,
            longitude: data.longitude || prev.longitude,
          }));
        }
      } catch (error) {
        console.error('연락처 데이터 로드 실패:', error);
      }
    };

    loadContactData();
  }, []);

  useEffect(() => {
    loadNaverMapScript();

    let mapInstance: any = null;
    let markerInstance: any = null;
    let infoWindowInstance: any = null;
    let initTimeout: NodeJS.Timeout;

    const initMap = () => {
      // Check if the container exists
      const mapContainer = document.getElementById("naver-map-contact");
      if (!mapContainer) {
        console.warn("Map container not found, retrying...");
        initTimeout = setTimeout(initMap, 100);
        return;
      }

      if (window.naver && window.naver.maps) {
        try {
          mapInstance = new window.naver.maps.Map("naver-map-contact", {
            center: new window.naver.maps.LatLng(contactData.latitude, contactData.longitude),
            zoom: 16,
          });

          markerInstance = new window.naver.maps.Marker({
            position: new window.naver.maps.LatLng(contactData.latitude, contactData.longitude),
            map: mapInstance,
          });

          infoWindowInstance = new window.naver.maps.InfoWindow({
            content: `
              <div style="padding:10px; font-size:14px; color:black;">
                <strong>${contactData.companyName}</strong><br/>
                ${contactData.address}
              </div>
            `,
          });

          infoWindowInstance.open(mapInstance, markerInstance);

          window.naver.maps.Event.addListener(markerInstance, "click", () => {
            if (infoWindowInstance.getMap()) {
              infoWindowInstance.close();
            } else {
              infoWindowInstance.open(mapInstance, markerInstance);
            }
          });
        } catch (error) {
          console.error("Error initializing Naver Map:", error);
        }
      } else {
        // If Naver Maps API is not loaded yet, retry after a short delay
        initTimeout = setTimeout(initMap, 100);
      }
    };

    // Start initialization after a delay to ensure DOM is ready
    initTimeout = setTimeout(initMap, 500);

    // Cleanup function
    return () => {
      if (initTimeout) {
        clearTimeout(initTimeout);
      }
      if (mapInstance) {
        try {
          mapInstance.destroy();
        } catch (error) {
          console.error("Error destroying map:", error);
        }
      }
    };
  }, [contactData]);

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        background: "#d82828",
        color: "#fff",
        padding: "2rem",
        position: "relative",
      }}
    >
      {/* Contact Title - 상단 중앙 */}
      <Typography
        variant="h4"
        sx={{
          fontSize: "2.5rem",
          fontWeight: "bold",
          textAlign: "center",
          marginBottom: isMobile ? "1rem" : "2rem",
          marginTop: "6rem",
        }}
      >
        {contactData.title}
      </Typography>

      {/* Content Section */}
      <Box
        sx={{
          display: isMobile ? "block" : "flex",
          width: "90%",
          maxWidth: "1200px",
          margin: "0 auto",
          justifyContent: "space-between",
          flexWrap: "wrap",
          marginTop: isMobile ? "2.5rem" : "5rem",
          gap: isMobile ? "2rem" : "0",
        }}
      >
        {/* Left Section - Contact Info */}
        <Box
          sx={{
            width: isMobile ? "100%" : "50%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: "2rem",
            paddingRight: isMobile ? "0" : "2rem",
          }}
        >
          {/* Address and Contact Info */}
          <Box>
            <Typography
              sx={{
                fontSize: "1.5rem",
                fontWeight: "bold",
                marginBottom: "0.5rem",
              }}
            >
              Address
            </Typography>
            <Typography sx={{ fontSize: "1.25rem" }}>
              {contactData.address}
            </Typography>
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: "1.5rem",
                fontWeight: "bold",
                marginBottom: "0.5rem",
              }}
            >
              E-mail & Phone
            </Typography>
            <Typography sx={{ fontSize: "1.25rem" }}>
              E: {contactData.email}
            </Typography>
            <Typography sx={{ fontSize: "1.25rem" }}>
              T: {contactData.phone}
            </Typography>
          </Box>

          {/* Button */}
          <Box
            onClick={() => {
              navigate("/contact");
              setTimeout(() => {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }, 100);
            }}
            sx={{
              textDecoration: "none",
              padding: theme.spacing(1, 3),
              color: "#fff",
              border: "0.1rem solid #fff",
              borderRadius: "1.875rem",
              fontSize: isMobile ? "0.7rem" : "1rem",
              fontWeight: "400",
              textAlign: "center",
              transition: "background-color 0.3s, border-color 0.3s",
              backgroundColor: "transparent",
              ":hover": {
                backgroundColor: "#fff",
                borderColor: "#fff",
                color: "#d82828",
              },
              width: "auto",
              display: "inline-block",
              maxWidth: "fit-content",
              cursor: "pointer",
            }}
          >
            {contactData.buttonText}
          </Box>
        </Box>

        {/* Right Section - 네이버 지도 */}
        <Box
          sx={{
            width: isMobile ? "100%" : "50%",
            height: isMobile ? "300px" : "400px",
            marginTop: isMobile ? "2.5rem" : "0rem",
            maxHeight: "400px",
            borderRadius: "8px",
            overflow: "hidden",
            background: "#fff",
          }}
        >
          <div
            id="naver-map-contact"
            style={{
              width: "100%",
              height: "100%",
            }}
          ></div>
        </Box>
      </Box>
    </Box>
  );
};

export default Contact;
